import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Cookie from "js-cookie";
import { SendIcon } from "lucide-react";
import "./LiveChatSection.scss";
import ExampleMessageFeed from "../examples/ExampleMessageFeed";
import Message from "../../../../i18n/Message";
import Spinner from "../../../../assets/icons/Spinner";
import { useManage } from "../context/ManageContext";
import backend from "../../../../backend/backend";

const CLIENT_ID_COOKIE = "mcpshowcase_client_id";

const genUid = () =>
  (window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : "m" + Date.now() + Math.floor(Math.random() * 1e6));

const getClientId = () => {
  let id = Cookie.get(CLIENT_ID_COOKIE);
  if (!id) {
    id = genUid();
    Cookie.set(CLIENT_ID_COOKIE, id, { expires: 365 * 5, path: "/", sameSite: "lax" });
  }
  return id;
};

const parsePrompts = (json) => {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
};

const LiveChatSection = ({ playground }) => {
  const manage = useManage();

  // Always render the real interactive runtime. The intro/prompts editor moved to the
  // details-page Settings tab. When `manage` is true this is the owner's own preview, so
  // route through the authenticated preview endpoints (excluded from analytics + quota).
  return <LiveChatRuntime playground={playground} preview={manage} />;
};

// ---- Interactive runtime --------------------------------------------------

const LiveChatRuntime = ({ playground, preview = false }) => {
  const intl = useIntl();
  const intro = playground.liveChatIntro;
  const prompts = useMemo(
    () => parsePrompts(playground.liveChatPromptsJson),
    [playground.liveChatPromptsJson]
  );

  const [messages, setMessages] = useState(null); // null = not started
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const sessionRef = useRef(null);
  const abortRef = useRef(null);
  // Synchronous re-entrancy lock. `busy` state lags a render, so two fast invocations
  // (double-Enter, two prompt bubbles) could both pass a `busy`-state check and open two
  // sessions/streams. This ref flips immediately, closing that window.
  const busyRef = useRef(false);
  // Hidden bot-trap input (public path only): real visitors never fill it. A non-empty value
  // is forwarded to the backend, which rejects the request.
  const honeypotRef = useRef(null);

  // Abort the in-flight SSE stream if the section unmounts (e.g. the visitor switches
  // tabs mid-answer) so we stop consuming the stream instead of updating a dead component.
  useEffect(() => () => abortRef.current && abortRef.current.abort(), []);

  const ensureSession = async (honeypot = "") => {
    if (sessionRef.current) return sessionRef.current;
    const clientId = getClientId();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const lang = navigator.language;
    // Preview is authenticated (owner) and takes no honeypot; the public widget does.
    const res = preview
      ? await backend.startLiveChatPreviewSession(playground.uid, clientId, lang, tz)
      : await backend.startLiveChatSession(playground.uid, clientId, lang, tz, honeypot);
    sessionRef.current = res.sessionUid;
    return res.sessionUid;
  };

  const send = async (text) => {
    const trimmed = (text || "").trim();
    // Ref lock (not the lagging `busy` state) guards against a synchronous double-submit.
    if (!trimmed || busyRef.current) return;
    busyRef.current = true;
    setNotice(null);
    setInput("");
    setBusy(true);
    // A new turn supersedes any stream still winding down; stop consuming it.
    if (abortRef.current) abortRef.current.abort();

    const honeypot = honeypotRef.current ? honeypotRef.current.value : "";

    // Client-side backstop: if the server never closes the stream (hung socket that even the
    // server-side 180s SseEmitter timeout doesn't propagate), abort and surface an error rather
    // than leaving the widget disabled indefinitely. Sits just above the server timeout so it
    // never cuts a turn the server is still legitimately streaming.
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      if (abortRef.current) abortRef.current.abort();
    }, 190000);

    // Build a mutable working array; append the visitor turn immediately.
    const working = [
      ...(messages || []),
      { uid: genUid(), role: "user", content: [{ type: "text", text: trimmed }] },
    ];
    setMessages([...working]);

    let afterToolResult = false;
    // Raised from within onEvent when the backend reports the session is gone (e.g. server
    // restart). Handled after the stream ends: recreate a fresh session and resend once.
    let sessionLost = false;
    const onEvent = (type, data) => {
      if (type === "text" || type === "tool_use") {
        const last = working[working.length - 1];
        if (!last || last.role !== "assistant" || afterToolResult) {
          working.push({ uid: genUid(), role: "assistant", content: [] });
          afterToolResult = false;
        }
        const cur = working[working.length - 1];
        if (type === "text") {
          cur.content.push({ type: "text", text: data.text });
        } else {
          cur.content.push({
            type: "tool_use",
            id: data.id,
            name: data.name,
            input: data.input,
          });
        }
        setMessages([...working]);
      } else if (type === "tool_result") {
        const block = {
          type: "tool_result",
          tool_use_id: data.tool_use_id,
          content: data.content,
          is_error: data.is_error,
        };
        const last = working[working.length - 1];
        // Group all tool_results of one assistant step into a single user message so
        // ExampleToolUseBlock (which only checks messages[idx+1]) pairs every tool_use.
        if (afterToolResult && last && last.role === "user") {
          last.content.push(block);
        } else {
          working.push({ uid: genUid(), role: "user", content: [block] });
          afterToolResult = true;
        }
        setMessages([...working]);
      } else if (type === "limit_reached") {
        setNotice("limit");
      } else if (type === "done") {
        // End-of-turn marker; busy is cleared in the finally below. Nothing else needed.
      } else if (type === "error") {
        if (data && data.code === "session_not_found") {
          sessionLost = true; // recover after the stream ends instead of surfacing an error
        } else {
          setNotice("error");
        }
      }
    };

    const streamTurn = (sessionUid, clientId) => {
      abortRef.current = new AbortController();
      return preview
        ? backend.streamLiveChatPreviewMessage(
            playground.uid,
            sessionUid,
            trimmed,
            clientId,
            onEvent,
            abortRef.current.signal
          )
        : backend.streamLiveChatMessage(
            sessionUid,
            trimmed,
            clientId,
            onEvent,
            abortRef.current.signal,
            honeypot
          );
    };

    try {
      const clientId = getClientId();
      await streamTurn(await ensureSession(honeypot), clientId);

      // Session expired/unknown: discard it, mint a fresh one, and resend exactly once.
      // Safe because the backend emits `session_not_found` before any turn content, so
      // `working` still ends at the user turn and the resend appends the reply without
      // duplicating it. Reset the grouping flag defensively so the fresh stream starts clean.
      if (sessionLost) {
        sessionLost = false;
        afterToolResult = false;
        sessionRef.current = null;
        await streamTurn(await ensureSession(honeypot), clientId);
        // If the fresh session still reports lost, treat it as a real error.
        if (sessionLost) setNotice("error");
      }
    } catch (e) {
      if (!timedOut) setNotice("error");
    } finally {
      clearTimeout(timer);
      if (timedOut) setNotice("error");
      busyRef.current = false;
      setBusy(false);
    }
  };

  const started = messages !== null;

  return (
    <div className="live_chat_section">
      {!started && (
        <div className="live_chat_intro">
          <p className="intro_text">
            {intro || <Message id="widget_live_chat_default_intro" />}
          </p>
          {prompts.length > 0 && (
            <div className="prompt_bubbles">
              {prompts.map((p, i) => (
                <button
                  key={i}
                  className="prompt_bubble"
                  disabled={busy}
                  onClick={() => send(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {started && <ExampleMessageFeed messages={messages} />}
      {busy && (
        <div className="live_chat_thinking">
          <Spinner size={16} /> <Message id="widget_live_chat_thinking" />
        </div>
      )}
      {notice === "limit" && (
        <div className="live_chat_notice">
          <Message id="widget_live_chat_unavailable" />
        </div>
      )}
      {notice === "error" && (
        <div className="live_chat_notice error">
          <Message id="widget_live_chat_error" />
        </div>
      )}

      <form
        className="live_chat_input_row"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        {/* Bot trap: off-screen, out of tab order, never announced. Real users leave it empty.
            Non-dictionary name so browser/password-manager autofill can't fill it (which would
            false-positive-block a real visitor). The name attribute is not sent — only the value. */}
        <input
          ref={honeypotRef}
          type="text"
          name="mcpsc_hp"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          defaultValue=""
          style={{
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            opacity: 0,
          }}
        />
        <input
          className="live_chat_input"
          value={input}
          disabled={busy}
          onChange={(e) => setInput(e.target.value)}
          placeholder={intl.formatMessage({ id: "widget_live_chat_input_placeholder" })}
        />
        <button className="live_chat_send" type="submit" disabled={busy || !input.trim()}>
          <SendIcon size={18} />
        </button>
      </form>
    </div>
  );
};

export default LiveChatSection;
